import type { DnscEvidence, DnscRequirement } from '../api/overview'

/**
 * Socotelile ecranului DNSC (`Nis2Dnsc`), fara React: tonul notei, eticheta
 * unei dovezi, rezumatul si filtrele.
 *
 * Tonul unei dovezi se ia din NOTA ei, pe aceeasi scara ca nota cerintei
 * (1-2 rosu, 3 portocaliu, 4-5 verde, fara nota gri). Pana pe 13 octombrie
 * 2026 se lua din verdictul regulii, si "Firewall on: 282 of 295 (96%) (fail;
 * 4% exceptions)" iesea ROSU langa un 4 / 5 verde: un auditor citea "fail".
 */

export type Tone = 'red' | 'orange' | 'green' | 'grey'
export type EvidenceState = 'ok' | 'warn' | 'bad' | 'unknown' | 'info'

export const LEVELS: Record<string, string> = { B: 'Basic', I: 'Important', E: 'Essential' }

/** Functiile NIST CSF 2.0, in ordinea lor. */
export const FUNCTIONS: Array<{ code: string; name: string }> = [
  { code: 'GV', name: 'Govern' },
  { code: 'ID', name: 'Identify' },
  { code: 'PR', name: 'Protect' },
  { code: 'DE', name: 'Detect' },
  { code: 'RS', name: 'Respond' },
  { code: 'RC', name: 'Recover' },
]

export function functionOf(id: string): string {
  return id.split('.')[0] ?? ''
}

export function toneOf(score: number | null | undefined): Tone {
  if (score === null || score === undefined) return 'grey'
  if (score >= 4) return 'green'
  if (score === 3) return 'orange'
  return 'red'
}

/** Ce se numara la fiecare control, cand e sigur; altfel "x of y". */
const NOUNS: Record<string, [string, string]> = {
  'sec.firewall': ['machine', 'machines'],
  'sec.encryption': ['machine', 'machines'],
  'security.no_antivirus': ['machine', 'machines'],
  'sec.protection': ['protection', 'protections'],
  'backup.covered': ['machine', 'machines'],
  'backup.fresh': ['machine', 'machines'],
  'fleet.agents': ['machine', 'machines'],
  'access.daily_admin': ['machine', 'machines'],
  'access.many_admins': ['machine', 'machines'],
  'access.shared_accounts': ['machine', 'machines'],
  'access.remote_tools': ['machine', 'machines'],
}

function short(n: number): string {
  const r = Math.round(n * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

function counted(key: string, n: number, total: number | null | undefined): string {
  const noun = NOUNS[key]
  if (noun) return `${n} ${n === 1 ? noun[0] : noun[1]}`
  return total ? `${n} of ${total}` : String(n)
}

function stateOf(e: DnscEvidence): EvidenceState {
  if (e.verdict === 'not known') return 'unknown'
  if (e.verdict === 'observed') return 'info'
  if (e.score === null) return e.verdict === 'pass' ? 'ok' : 'bad'
  const tone = toneOf(e.score)
  return tone === 'green' ? 'ok' : tone === 'orange' ? 'warn' : 'bad'
}

/**
 * Dovada intr-un rand: starea (iconita si culoarea) si textul.
 *
 * Cu procent de exceptii: "96% compliant - 4% exceptions (13 machines)". Fara:
 * cifra, si regula spusa in cuvinte. Necitita: "not measured - <de ce>".
 */
export function evidenceLine(e: DnscEvidence): { state: EvidenceState; text: string } {
  const state = stateOf(e)

  if (e.verdict === 'not known') {
    return { state, text: `not measured - ${e.because || 'the reading did not come back'}` }
  }

  if (e.verdict === 'observed') {
    return { state, text: `${e.figure} - observed only, no rule decides it` }
  }

  const value = typeof e.value === 'number' ? e.value : null
  const total = typeof e.total === 'number' && e.total > 0 ? e.total : null

  if (e.exceptions !== null && e.exceptions !== undefined && (e.rule === 'coverage' || e.rule === 'zero')) {
    const off = e.rule === 'coverage' ? (total !== null && value !== null ? total - value : null) : value
    if (e.exceptions <= 0) {
      return { state, text: total !== null ? `100% compliant - no exceptions (${counted(e.key, total, null)} checked)` : 'compliant - none found' }
    }
    const tail = off === null ? '' : ` (${counted(e.key, off, total)})`
    return { state, text: `${short(100 - e.exceptions)}% compliant - ${short(e.exceptions)}% exceptions${tail}` }
  }

  if (e.score === 2 && e.exceptions === null && e.rule === 'zero') {
    return { state, text: `${e.figure} found - the share cannot be counted without a population` }
  }

  return { state, text: e.because ? `${e.figure} - ${e.because}` : e.figure }
}

/** De ce o cerinta nu are nota - pentru tooltip. */
export function whyNotMeasured(r: DnscRequirement): string {
  const reasons = r.evidence
    .filter((e) => e.score === null)
    .map((e) => `${e.title}: ${e.verdict === 'not known' ? e.because || 'not read' : 'a figure without a rule proposes nothing'}`)
  return reasons.length > 0
    ? `No control behind this requirement could be scored. ${reasons.join('; ')}.`
    : 'No control behind this requirement could be scored.'
}

export interface DnscSummary {
  total: number
  scored: number
  average: number | null
  keyBelow3: number
  notMeasured: number
}

export function summarise(requirements: DnscRequirement[]): DnscSummary {
  const scores = requirements.map((r) => r.score).filter((s): s is number => s !== null)
  return {
    total: requirements.length,
    scored: scores.length,
    average: scores.length > 0 ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10 : null,
    keyBelow3: requirements.filter((r) => r.key && r.score !== null && r.score < 3).length,
    notMeasured: requirements.length - scores.length,
  }
}

export type LevelFilter = 'all' | 'B' | 'I' | 'E'
export type ScoreFilter = 'all' | 'below3' | 'unmeasured'

export interface DnscFilters {
  level: LevelFilter
  keyOnly: boolean
  score: ScoreFilter
}

export const NO_FILTERS: DnscFilters = { level: 'all', keyOnly: false, score: 'all' }

export function applyFilters(requirements: DnscRequirement[], f: DnscFilters): DnscRequirement[] {
  return requirements.filter((r) => {
    if (f.level !== 'all' && r.level !== f.level) return false
    if (f.keyOnly && !r.key) return false
    if (f.score === 'below3' && !(r.score !== null && r.score < 3)) return false
    if (f.score === 'unmeasured' && r.score !== null) return false
    return true
  })
}

/** Cerintele pe functii NIST, in ordinea GV, ID, PR, DE, RS, RC; goalele lipsesc. */
export function byFunction(requirements: DnscRequirement[]): Array<{ code: string; name: string; items: DnscRequirement[] }> {
  const known = FUNCTIONS.map((fn) => ({ ...fn, items: requirements.filter((r) => functionOf(r.id) === fn.code) }))
  const other = requirements.filter((r) => !FUNCTIONS.some((fn) => fn.code === functionOf(r.id)))
  const groups = known.filter((g) => g.items.length > 0)
  return other.length > 0 ? [...groups, { code: 'other', name: 'Other', items: other }] : groups
}
